import { yupResolver } from '@hookform/resolvers/yup';
import { FormFieldError, TextInput } from '@kira/checkpoint-shared-frontend';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import * as yup from 'yup';

import Banner, { BannerVariant } from 'components/Banner/Banner';
import FormControls from 'components/FormControls/FormControls';
import InlineForm from 'components/InlineForm/InlineForm';
import MediaUploader, {
  MediaType,
} from 'components/MediaUploader/MediaUploader';
import Panel from 'components/Panel/Panel';
import SelectMenu, { SelectMenuOption } from 'components/SelectMenu/SelectMenu';
import TextArea from 'components/TextArea/TextArea';
import useCreateQuestion from 'hooks/useCreateQuestion';
import useGetCompetencyDetails, {
  Question,
  QuestionType,
} from 'hooks/useGetCompetencyDetails';
import useNotification from 'hooks/useNotification';
import useUpdateQuestion from 'hooks/useUpdateQuestions';
import { t } from 'locales/i18n';
import { secondsToTime } from 'utils/secondsToTime';
import { timeToSeconds } from 'utils/timeToSeconds';

enum FormFields {
  QUESTION_TYPE = 'questionType',
  QUESTION_TEXT = 'questionText',
  PREPARATION_TIME = 'preparationTime',
  RESPONSE_TIME = 'responseTime',
  WORD_LIMIT = 'wordLimit',
}

const QuestionTypeOptions = [
  {
    value: QuestionType.VIDEO,
    primaryText: t('Video'),
  },
  {
    value: QuestionType.WRITTEN,
    primaryText: t('Written'),
  },
] as SelectMenuOption[];

interface FormData {
  [FormFields.QUESTION_TYPE]: QuestionType;
  [FormFields.QUESTION_TEXT]: string;
  [FormFields.PREPARATION_TIME]: string;
  [FormFields.RESPONSE_TIME]: string;
  [FormFields.WORD_LIMIT]: number;
}

interface CompetencyQuestionsFormProps {
  closeForm: () => void;
  initialValue?: Question;
  showBanner?: boolean;
  updateAndAddAnother?: () => void;
}

const CompetencyQuestionsForm = ({
  initialValue,
  closeForm,
  showBanner,
  updateAndAddAnother,
}: CompetencyQuestionsFormProps) => {
  const queryClient = useQueryClient();
  const { interviewUid = '', competencyUid = '' } = useParams();
  const { createQuestion, isLoading } = useCreateQuestion(competencyUid);
  const { updateQuestion, isLoading: isLoadingUpdateQuestion } =
    useUpdateQuestion(initialValue?.uid || '');
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const [fileUploaded, setFileUploaded] = useState<Blob | undefined>();
  const [isMediaDeleted, setIsMediaDeleted] = useState(false);
  const [bannerMessage, setBannerMessage] = useState('');
  const [isSavingAndAddingAnother, setIsSavingAndAddingAnother] =
    useState(false);
  const { data: competencyDetails } = useGetCompetencyDetails(
    interviewUid,
    competencyUid
  );
  const textAreaRef = React.createRef<HTMLTextAreaElement>();

  const firstQuestion = competencyDetails?.questions[0];

  const getDefaultResponseTime = (questionType: string | number) =>
    questionType === QuestionType.VIDEO ? 60 : 300;

  const defaultFormValues = useMemo(() => {
    const defaultQuestionType =
      firstQuestion?.question_type || QuestionType.VIDEO;

    return {
      [FormFields.QUESTION_TEXT]: initialValue?.question_text || '',
      [FormFields.PREPARATION_TIME]: secondsToTime(
        initialValue?.preparation_time || 30
      ),
      [FormFields.QUESTION_TYPE]: defaultQuestionType,
      [FormFields.RESPONSE_TIME]: secondsToTime(
        initialValue?.response_time ||
          getDefaultResponseTime(defaultQuestionType)
      ),
      [FormFields.WORD_LIMIT]: initialValue?.word_limit || undefined,
    };
  }, [firstQuestion, initialValue]);

  const FormSchema = useMemo(
    () =>
      yup.object().shape({
        [FormFields.QUESTION_TYPE]: yup
          .string()
          .required(t('Question type is required')),
        [FormFields.QUESTION_TEXT]: yup
          .string()
          .required(t('Question text is required')),
        [FormFields.PREPARATION_TIME]: yup
          .string()
          .matches(/^(?:(?:[0-5]?[0-9]):)?[0-5]?[0-9]:(?:[0-5]?[0-9])$/, {
            message: 'Invalid time format. Must be either hh:mm:ss or mm:ss',
            excludeEmptyString: true,
          })
          .required(t('Prep time is required')),
        [FormFields.RESPONSE_TIME]: yup
          .string()
          .matches(/^(?:(?:[0-5]?[0-9]):)?[0-5]?[0-9]:(?:[0-5]?[0-9])$/, {
            message: 'Invalid time format. Must be either hh:mm:ss or mm:ss',
            excludeEmptyString: true,
          })
          .required(t('Response time is required')),
        [FormFields.WORD_LIMIT]: yup
          .number()
          .positive(t('Word limit must be a positive number'))
          .nullable()
          .transform((_, val) => (val !== '' ? Number(val) : null)),
      }),
    [
      FormFields.PREPARATION_TIME,
      FormFields.QUESTION_TEXT,
      FormFields.QUESTION_TYPE,
      FormFields.RESPONSE_TIME,
      FormFields.WORD_LIMIT,
    ]
  );

  const {
    handleSubmit,
    formState: { errors },
    watch,
    register,
    reset,
    setValue,
    control,
  } = useForm<FormData>({
    resolver: yupResolver(FormSchema),
    mode: 'onSubmit',
    defaultValues: defaultFormValues,
  });

  useEffect(() => {
    watch();
  }, []);

  const onSubmit: SubmitHandler<FormData> = async ({
    preparationTime,
    questionText,
    questionType,
    responseTime,
    wordLimit,
  }) => {
    textAreaRef?.current?.focus();
    await createQuestion(
      {
        preparationTime: timeToSeconds(preparationTime),
        questionText,
        questionType,
        responseTime: timeToSeconds(responseTime),
        wordLimit,
        video: fileUploaded,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries([
            'getCompetencyDetails',
            interviewUid,
            competencyUid,
          ]);
          queryClient.invalidateQueries([
            'getAssessmentStepDetails',
            interviewUid,
          ]);
          showSuccessNotification(
            t('Question Added'),
            t('Your question was successfully added')
          );

          if (!isSavingAndAddingAnother) {
            closeForm();
            return;
          }

          setValue(FormFields.QUESTION_TEXT, '');
          setFileUploaded(undefined);
        },
        onError: (error) => {
          showErrorNotification(
            t('There was an error creating the question.'),
            error.message
          );
        },
      }
    );
  };

  const onSubmitUpdateQuestion: SubmitHandler<FormData> = async ({
    preparationTime,
    questionText,
    responseTime,
    wordLimit,
  }) => {
    if (!initialValue || !initialValue.uid) return;
    await updateQuestion(
      {
        preparationTime: timeToSeconds(preparationTime),
        questionText,
        responseTime: timeToSeconds(responseTime),
        wordLimit,
        video: fileUploaded,
        deleteVideo: initialValue && isMediaDeleted && !fileUploaded,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries([
            'getCompetencyDetails',
            interviewUid,
            competencyUid,
          ]);
          queryClient.invalidateQueries([
            'getAssessmentStepDetails',
            interviewUid,
          ]);
          showSuccessNotification(
            t('Question Updated'),
            t('Your question was successfully updated.')
          );
          closeForm();
          if (isSavingAndAddingAnother && updateAndAddAnother) {
            updateAndAddAnother();
          }
        },
        onError: (error) => {
          showErrorNotification(
            t('There was an error updating the question.'),
            error.message
          );
        },
      }
    );
  };

  const mediaUrl = () => {
    if (fileUploaded) {
      return URL.createObjectURL(fileUploaded);
    }
    if (initialValue?.video && !isMediaDeleted) {
      return initialValue.video;
    }
    return undefined;
  };

  const handleCancel = () => {
    closeForm();
    reset();
  };

  const isVideoQuestion =
    watch(FormFields.QUESTION_TYPE) === QuestionType.VIDEO;
  const isWrittenQuestion =
    watch(FormFields.QUESTION_TYPE) === QuestionType.WRITTEN;

  const textInputClassName = twMerge(
    'appearance-none',
    '!text-base',
    '!font-normal',
    '!text-kira-slate-900',
    '!p-1',
    '!border-kira-slate-200'
  );

  useMemo(() => {
    if (fileUploaded) {
      setBannerMessage(
        t(
          'Upon saving, the question video will need time to process. Please check back in a few minutes - you may need to refresh the page to play the video.'
        )
      );
    }

    if (showBanner) {
      setBannerMessage(
        t(
          'The question video is processing - please check back in a few minutes. You may need to refresh the page to play the video.'
        )
      );
    }

    setTimeout(() => {
      setBannerMessage('');
    }, 60000);
  }, [showBanner, fileUploaded]);

  return (
    <Panel isFocused focusedClassName="shadow-md">
      <form
        data-testid="competency-update-questions-form"
        onSubmit={
          initialValue
            ? handleSubmit(onSubmitUpdateQuestion)
            : handleSubmit(onSubmit)
        }
      >
        <fieldset className="flex flex-col gap-6" data-testid="questionForm">
          <InlineForm
            title={t('Question type')}
            description={t('Choose the question type for applicants.')}
          >
            <>
              <Controller
                control={control}
                name={FormFields.QUESTION_TYPE}
                render={({ field }) => (
                  <SelectMenu
                    options={QuestionTypeOptions}
                    dataQa="questionTypeOptions"
                    emptyText={t('Select assessment')}
                    value={QuestionTypeOptions.find(
                      (option) => option.value === field.value
                    )}
                    isDisabled={!!firstQuestion || !!initialValue}
                    onChange={(option) => {
                      field.onChange(option.value);
                      setValue(
                        FormFields.RESPONSE_TIME,
                        secondsToTime(getDefaultResponseTime(option.value))
                      );
                    }}
                  />
                )}
              />
              <FormFieldError
                isVisible={!!errors[FormFields.QUESTION_TYPE]}
                message={errors[FormFields.QUESTION_TYPE]?.message ?? ''}
                className="mt-2"
              />
            </>
          </InlineForm>
          <InlineForm
            title={t('Question text')}
            description={t(
              'Set the question text that will be visible to applicants.'
            )}
          >
            <Controller
              control={control}
              name={FormFields.QUESTION_TEXT}
              render={({ field }) => (
                <TextArea
                  id={`question-text-input-${initialValue?.uid}`}
                  value={field.value}
                  placeholder={t('Enter question text')}
                  onChange={(e) => field.onChange(e.target.value)}
                  dataTestId="questionText"
                  errorDataTestId="questionTextError"
                  errors={errors[FormFields.QUESTION_TEXT]}
                  autoFocus
                  ref={textAreaRef}
                />
              )}
            />
          </InlineForm>
          {isVideoQuestion && (
            <InlineForm
              title={t('Prep time')}
              description={t(
                'Choose how long applicants have to prepare before answering the question. Time is displayed in MM:SS.'
              )}
            >
              <TextInput
                value={FormFields.PREPARATION_TIME}
                placeholder={t('Enter preparation time')}
                type="text"
                data-testid="prepTimeInput"
                inputClassName={textInputClassName}
                {...register(FormFields.PREPARATION_TIME)}
              />
              <FormFieldError
                isVisible={!!errors[FormFields.PREPARATION_TIME]}
                message={errors[FormFields.PREPARATION_TIME]?.message ?? ''}
                className="mt-2"
                dataTestId="prepTimeError"
              />
            </InlineForm>
          )}
          <InlineForm
            title={t('Response time')}
            description={t(
              'Choose how long applicants have to respond to the question. Time is displayed in MM:SS.'
            )}
          >
            <>
              <TextInput
                value={FormFields.RESPONSE_TIME}
                placeholder={t('Enter response time')}
                type="text"
                data-testid="responseTimeInput"
                inputClassName={textInputClassName}
                {...register(FormFields.RESPONSE_TIME)}
              />
              <FormFieldError
                isVisible={!!errors[FormFields.RESPONSE_TIME]}
                message={errors[FormFields.RESPONSE_TIME]?.message ?? ''}
                className="mt-2"
                dataTestId="responseTimeError"
              />
            </>
          </InlineForm>
          {isWrittenQuestion && (
            <InlineForm
              title={t('Word limit for responses')}
              description={t(
                'Optionally set a word limit for applicant responses to this question.'
              )}
            >
              <>
                <TextInput
                  value={FormFields.WORD_LIMIT}
                  type="number"
                  data-testid="wordLimitInput"
                  inputClassName={textInputClassName}
                  {...register(FormFields.WORD_LIMIT)}
                />
                <FormFieldError
                  isVisible={!!errors[FormFields.WORD_LIMIT]}
                  message={errors[FormFields.WORD_LIMIT]?.message ?? ''}
                  className="mt-2"
                />
              </>
            </InlineForm>
          )}
          {isVideoQuestion && (
            <div>
              {!!bannerMessage && (
                <Banner variant={BannerVariant.INFO} message={bannerMessage} />
              )}
              <MediaUploader
                acceptFileType=".mp4"
                deleteConfirmation={t(
                  'Are you sure you want to delete this video?'
                )}
                deleteDescription={t(
                  "Deleting a video is permanent and can't be undone."
                )}
                deleteTitle={t('Delete Video')}
                description={t('Upload an .mp4 file under 50MB')}
                fileSizeLimit={50}
                isLoading={isLoading}
                mediaType={MediaType.VIDEO}
                mediaUrl={mediaUrl()}
                onUpload={(data: Blob) => setFileUploaded(data)}
                onDelete={async () => {
                  setFileUploaded(undefined);
                  setIsMediaDeleted(true);
                }}
              />
            </div>
          )}
          <FormControls
            shouldShowAddAnother
            isLoading={isLoading || isLoadingUpdateQuestion}
            onBeforeSubmit={() => setIsSavingAndAddingAnother(false)}
            onBeforeAddAnother={() => setIsSavingAndAddingAnother(true)}
            onCancel={handleCancel}
          />
        </fieldset>
      </form>
    </Panel>
  );
};

export default CompetencyQuestionsForm;
